<style lang="postcss">
	.range-slider-cover * {
		user-select: none;
	}
	.slider-error {
		display: flex;
		justify-content: center;
		align-items: center;
		color: var(--error-color);
		font-size: 14px;
		padding: 0.75rem 1rem;
		border-radius: 0.25rem;
		border: 1px solid var(--error-color);
	}
	.range-slider-cover {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		position: relative;
	}
	.range-pips-cover {
		position: absolute;
		top: 0.625rem;
		width: 100%;
		left: 0;
		right: 0;
		height: 2rem;
	}
	.range-slider {
		position: relative;
		display: flex;
		flex-direction: row;
		align-items: center;
		height: 1em;
		border-radius: 9999px;
		transition: opacity 200ms ease;
	}
	.range-slider-bar {
		height: 0.3rem;
		width: 100%;
		border-radius: 9999px;
		display: flex;
		align-items: center;
		flex-direction: row;
		background: rgba(200, 200, 200, 0.5);
	}
	.range-slider-bar-inner {
		height: 100%;
		width: 100%;
		margin: 0 0.5em;
		position: relative;
		display: flex;
		align-items: center;
		flex-direction: row;
	}
	.range-float {
		color: rgba(255, 255, 255, 0.9);
		font-size: 0.825rem;
		position: absolute;
		pointer-events: none;
		white-space: nowrap;
		display: flex;
		justify-content: center;
		border-radius: 0.25rem;
		align-items: center;
		padding: 0.25em 0.35em;
	}
	.range-handle {
		position: absolute;
		width: 1.5em;
		height: 1.5em;
		padding: 0.25em;
		border-radius: 100%;
		transform: translateY(0%) translateX(-50%);
	}
	.range-handle:focus {
		outline: none;
	}
	.range-nub {
		position: absolute;
		width: 1em;
		height: 1em;
		border-radius: 100%;
	}
	.range-nub:before {
		position: absolute;
		left: 0;
		top: 0;
		border-radius: 100%;
		width: 100%;
		height: 100%;
		transition: box-shadow 500ms ease;
	}
	.range-nub:before {
		content: "";
		left: 1px;
		top: 1px;
		bottom: 1px;
		right: 1px;
		height: auto;
		width: auto;
		opacity: 0;
	}

	.range-slider.hoverable:not(.disabled) .range-handle.press .range-nub:before,
	.range-slider.hoverable:not(.disabled)
		.range-handle.press:hover
		.range-nub:before {
		box-shadow: 0 0 0 0.5em rgba(0, 0, 0, 0.25);
		opacity: 0.4;
	}

	.range-slider.sm.hoverable:not(.disabled)
		.range-handle:hover
		.range-nub:before,
	.range-slider.sm.hoverable:not(.disabled)
		.range-handle.press:hover
		.range-nub:before {
		box-shadow: 0 0 0 0.3em rgba(0, 0, 0, 0.25);
		opacity: 0.4;
	}

	.range-slider-bar,
	.range-slider.disabled .range-nub {
		background: rgba(212, 212, 212, 1);
	}
	.range-slider .range-nub,
	.range-slider .range-float {
		background-color: #444;
	}
	.range-slider.focus .range-nub,
	.range-slider .range-handle.active .range-nub,
	.range-slider.focus .range-float {
		background-color: #333;
	}
	.range-slider.disabled {
		opacity: 0.5;
	}
</style>

<script lang="ts">
	import { tweened } from "svelte/motion";
	import { createEventDispatcher, onMount, tick } from "svelte";
	import RangePips from "./RangePips.svelte";
	import { createFloatingActions } from "svelte-floating-ui";
	import { flip, offset, shift } from "@floating-ui/dom";

	const [floatingRef, floatingContent] = createFloatingActions({
		strategy: "absolute",
		placement: "top",
		middleware: [
			offset(0),
			flip(),
			shift({
				padding: 2
			})
		]
	});

	type ClientPos = {
		clientX: number;
		clientY: number;
	};

	let slider: HTMLDivElement;

	export let prefix = "";
	export let suffix = "";
	export let leftLabel = "";
	export let rightLabel = "";
	export let min = 0;
	export let max = 100;
	export let step = 10;
	export let reversed = false;
	export let hoverable = true;
	export let disabled = false;
	export let options: ChoiceOption[] = [];
	export let type: SliderField["settings"]["type"] = "numbers";
	export let internalValue: number =
		type === "numbers" ? (max + min) / 2 : Math.round((options.length - 1) / 2);
	export let value: ChoiceValue = undefined;
	export let tooltip = true;
	export let name: string = "";

	$: fixType(type);

	const fixType = (type: SliderField["settings"]["type"]) => {
		if (type === "labels") {
			min = 0;
			max = options.length - 1;
			step = 1;
			internalValue = Math.round(internalValue);
		}
	};

	// range pips / internalValue props
	export let pips = true;

	// stylistic props
	let precision = 2;

	// prepare dispatched events
	const dispatch = createEventDispatcher();
	let gridPadding = 16;
	let mounted = false;

	// state management
	let valueLength = 0;
	let focus = false;
	let handleActivated = false;
	let handlePressed = false;
	let keyboardActive = false;
	let activeHandle: number = 0;
	let startValue: number;
	let previousValue: number;
	let orientationStart: "left" | "right" = "left";
	let orientationEnd: "left" | "right" = "right";
	let focusDelayHideTimeout: ReturnType<typeof setTimeout>;
	let focusDelay = true;

	let lastValue: number;

	const fixFloat = (v: number) => parseFloat(v.toFixed(precision));
	const hideDelay = 1000;

	let springPositions: ReturnType<typeof tweened<number>>;
	let springPositionsFloat: ReturnType<typeof tweened<number>>;

	$: changePositions(internalValue);

	function changePositions(internal: any) {
		internal = alignValueToStep(internal);
		if (valueLength === 0) {
			const percentValues = percentOf(internal);
			springPositions = tweened(percentValues, { duration: 0 });
			springPositionsFloat = tweened(percentValues, { duration: 0 });
		} else {
			const percentValues = percentOf(internal);
			springPositions.set(percentValues);
			springPositionsFloat.set(percentValues);
		}
		// set the valueLength for the next check
		valueLength = 1;
		lastValue = internal;
	}

	function percentOf(val: number) {
		let perc = ((val - min) / (max - min)) * 100;
		if (isNaN(perc) || perc <= 0) {
			return 0;
		} else if (perc >= 100) {
			return 100;
		} else {
			return fixFloat(perc);
		}
	}

	function clampValue(val: number) {
		return val <= min ? min : val >= max ? max : val;
	}

	function alignValueToStep(val: number) {
		if (val <= min) {
			return fixFloat(min);
		} else if (val >= max) {
			return fixFloat(max);
		}
		let remainder = (val - min) % step;
		let aligned = val - remainder;
		if (Math.abs(remainder) * 2 >= step) {
			aligned += remainder > 0 ? step : -step;
		}
		aligned = clampValue(aligned);
		return fixFloat(aligned);
	}

	$: orientationStart = reversed ? "right" : "left";
	$: orientationEnd = reversed ? "left" : "right";

	function normalisedClient(e: MouseEvent | TouchEvent): ClientPos {
		if (e.type.includes("touch") && "touches" in e) {
			return {
				clientX: e.touches[0].clientX,
				clientY: e.touches[0].clientY
			};
		} else if (e instanceof MouseEvent) {
			return {
				clientX: e.clientX,
				clientY: e.clientY
			};
		} else {
			return {
				clientX: 0,
				clientY: 0
			};
		}
	}

	function targetIsHandle(el: Element) {
		const handles = slider.querySelectorAll(".handle");
		const isHandle = Array.prototype.includes.call(handles, el);
		const isChild = Array.prototype.some.call(handles, (e) => e.contains(el));
		return isHandle || isChild;
	}

	function getSliderDimensions() {
		return slider.getBoundingClientRect();
	}

	function handleInteract(clientPos: ClientPos) {
		const dims = getSliderDimensions();
		let handlePos = 0;
		let handlePercent = 0;
		let handleVal = 0;
		handlePos = clientPos.clientX - dims.left;
		handlePercent = (handlePos / dims.width) * 100;
		handlePercent = reversed ? 100 - handlePercent : handlePercent;
		handleVal = ((max - min) / 100) * handlePercent + min;
		moveHandle(handleVal);
	}

	function moveHandle(value: number) {
		value = alignValueToStep(value);
		if (internalValue !== value) {
			internalValue = value;
		}
		if (previousValue !== value) {
			eChange();
			previousValue = value;
		}
		return value;
	}

	function sliderBlurHandle(e: Event) {
		if (keyboardActive) {
			focus = false;
			handleActivated = false;
			handlePressed = false;
		}
		dispatch("blur");
		clearTimeout(focusDelayHideTimeout);
		focusDelayHideTimeout = setTimeout(() => {
			focusDelay = false;
		}, hideDelay);
	}

	function sliderFocusHandle(e: FocusEvent) {
		if (!disabled) {
			activeHandle = 0;
			focus = true;
			dispatch("focus");
		}
	}

	function sliderKeydown(e: KeyboardEvent) {
		if (!disabled) {
			let jump = e.ctrlKey || e.metaKey || e.shiftKey ? step * 10 : step;
			let prevent = false;

			switch (e.key) {
				case "PageDown":
					jump *= 10;
				case "ArrowRight":
				case "ArrowUp":
					moveHandle(internalValue + jump);
					prevent = true;
					break;
				case "PageUp":
					jump *= 10;
				case "ArrowLeft":
				case "ArrowDown":
					moveHandle(internalValue - jump);
					prevent = true;
					break;
				case "Home":
					moveHandle(min);
					prevent = true;
					break;
				case "End":
					moveHandle(max);
					prevent = true;
					break;
			}
			if (prevent) {
				e.preventDefault();
				e.stopPropagation();
			}
		}
	}

	function sliderInteractStart(e: MouseEvent | TouchEvent) {
		if (!disabled) {
			const el = e.target as Element;
			const clientPos = normalisedClient(e);

			focus = true;
			handleActivated = true;
			handlePressed = true;
			activeHandle = 0;
			clearTimeout(focusDelayHideTimeout);
			focusDelay = true;

			startValue = previousValue = alignValueToStep(internalValue);

			if (e.type === "touchstart" && !el.matches(".pipVal")) {
				handleInteract(clientPos);
			}
		}
	}

	function sliderInteractEnd(e: Event) {
		handlePressed = false;
		clearTimeout(focusDelayHideTimeout);
		focusDelayHideTimeout = setTimeout(() => {
			focusDelay = false;
		}, hideDelay);
	}

	function bodyInteractStart(e: MouseEvent | TouchEvent) {
		keyboardActive = false;
		if (focus && e.target !== slider && !slider.contains(e.target as Element)) {
			focus = false;
		}
	}

	function bodyInteract(e: MouseEvent | TouchEvent) {
		if (!disabled) {
			if (handleActivated) {
				handleInteract(normalisedClient(e));
			}
		}
	}
	function bodyMouseUp(e: MouseEvent) {
		if (!disabled) {
			const el = e.target as Element;
			if (handleActivated) {
				if (el === slider || slider.contains(el)) {
					focus = true;
					if (!targetIsHandle(el) && !el.matches(".pipVal")) {
						handleInteract(normalisedClient(e));
					}
				}
			}
		}
		handleActivated = false;
		handlePressed = false;
		if (value) {
			clearTimeout(focusDelayHideTimeout);
			focusDelayHideTimeout = setTimeout(() => {
				focusDelay = false;
			}, hideDelay);
		}
	}

	function bodyTouchEnd(e: MouseEvent | TouchEvent) {
		handleActivated = false;
		handlePressed = false;
		clearTimeout(focusDelayHideTimeout);
		focusDelayHideTimeout = setTimeout(() => {
			focusDelay = false;
		}, hideDelay);
	}

	function bodyKeyDown(e: KeyboardEvent) {
		if (!disabled && slider) {
			if (e.target === slider || slider.contains(e.target as Element)) {
				keyboardActive = true;
			}
		}
	}

	function display(value: number, expand: boolean = false): string {
		function roundToSameDecimalPlaces(number1, number2) {
			const decimalPlaces = (number1.toString().split(".")[1] || "").length;
			return Number(number2.toFixed(decimalPlaces));
		}
		if (type === "labels" && !isNaN(value)) {
			return options[value]?.label ?? "";
		} else {
			return typeof value !== "undefined"
				? expand
					? `${prefix} ${value} ${suffix}`
					: roundToSameDecimalPlaces(step, value).toString()
				: "";
		}
	}

	function eChange() {
		if (type === "labels" && !isNaN(internalValue)) {
			const shouldBe = options[internalValue];
			if (shouldBe !== value) {
				value = shouldBe;
				dispatch("change", value);
			}
		} else {
			if (isNaN(internalValue) || internalValue < min || internalValue > max) {
				value = undefined;
			} else {
				const shouldBe = {
					label: `${prefix}${internalValue}${suffix}`,
					value: internalValue.toString()
				};
				if (shouldBe.label !== value?.label) {
					value = shouldBe;
					dispatch("change", value);
				}
			}
		}
	}

	$: changeValue(value);

	function changeValue(value: any) {
		if (!value || !value.value) return;
		if (type === "labels") {
			const shouldBe = options.findIndex((o) => o.value === value.value);
			if (internalValue !== shouldBe) {
				internalValue = shouldBe;
				eChange();
			}
		} else {
			const shouldBe = parseFloat(value?.value);
			if (internalValue !== shouldBe) {
				internalValue = shouldBe;
				eChange();
			}
		}
		changePositions(internalValue);
	}

	onMount(async () => {
		if (value) {
			if (type === "labels") {
				internalValue = options.findIndex((o) => o.value === value.value);
			} else {
				internalValue = parseFloat(value.value);
			}
			eChange();
			await tick();
		}
		mounted = true;
		if (document.getElementsByClassName("field-grid")?.[0]) {
			const style = getComputedStyle(
				document.getElementsByClassName("field-grid")[0]
			);
			gridPadding = parseFloat(style.paddingLeft) * 0.75;
		}
	});
	$: pipsKey = `${leftLabel}-${rightLabel}-${min}-${max}-${step}`;
</script>

<div
	class="range-slider-cover"
	style="margin-top: {tooltip ? '1.5rem' : '0'}; padding-bottom: {tooltip
		? '2rem'
		: ''}">
	{#if max < min}
		<div class="slider-error">
			<p>
				Invalid field configuration. Max value must be greater than min value.
			</p>
		</div>
	{:else}
		{#if pips}
			{#key pipsKey}
				<div class="range-pips-cover">
					<RangePips
						type="{type}"
						display="{display}"
						focusDelay="{focusDelay}"
						internalValue="{internalValue}"
						leftLabel="{leftLabel}"
						rightLabel="{rightLabel}"
						value="{value}"
						min="{min}"
						max="{max}"
						step="{step}"
						reversed="{reversed}"
						orientationStart="{orientationStart}"
						hoverable="{hoverable}"
						disabled="{disabled}"
						focus="{focus}"
						percentOf="{percentOf}"
						moveHandle="{moveHandle}"
						fixFloat="{fixFloat}" />
				</div>
			{/key}
		{/if}
		{#if type === "labels"}
			<input
				name="{name}"
				type="hidden"
				value="{options[internalValue]?.value ?? ''}" />
		{:else}
			<input name="{name}" type="hidden" value="{internalValue}" />
		{/if}
		<div
			role="button"
			bind:this="{slider}"
			tabindex="-1"
			class="range-slider"
			class:disabled="{disabled}"
			class:hoverable="{hoverable}"
			class:reversed="{reversed}"
			class:focus="{focus}"
			class:pips="{pips}"
			on:mousedown="{sliderInteractStart}"
			on:mouseup="{sliderInteractEnd}"
			on:touchstart|preventDefault="{sliderInteractStart}"
			on:touchend|preventDefault="{sliderInteractEnd}">
			<div class="range-slider-bar">
				<div class="range-slider-bar-inner">
					{#if value && value.label && mounted && tooltip}
						<span
							class="range-float"
							use:floatingContent
							class:active="{focus}">
							{display(internalValue, true)}
						</span>
					{/if}
					<span
						role="slider"
						title="range handle"
						aria-label="range handle"
						class="range-handle"
						class:active="{focus}"
						class:press="{handlePressed}"
						use:floatingRef
						on:blur="{sliderBlurHandle}"
						on:focus="{sliderFocusHandle}"
						on:keydown="{sliderKeydown}"
						style="{orientationStart}: {$springPositions}%"
						aria-valuemin="{min}"
						aria-valuemax="{max}"
						aria-valuenow="{internalValue}"
						aria-orientation="horizontal"
						tabindex="{disabled ? -1 : 0}">
						<span class="range-nub"></span>
					</span>
				</div>
			</div>
		</div>
	{/if}
</div>

<svelte:window
	on:mousedown="{bodyInteractStart}"
	on:touchstart="{bodyInteractStart}"
	on:mousemove="{bodyInteract}"
	on:touchmove="{bodyInteract}"
	on:mouseup="{bodyMouseUp}"
	on:touchend="{bodyTouchEnd}"
	on:keydown="{bodyKeyDown}" />
